import React, { SyntheticEvent, useContext, useState } from "react";

import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  ListItem,
  Snackbar,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useParams } from "react-router-dom";

import {
  ChargifyState,
  PaymentMethod,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_payment_pb";
import {
  Role,
  SubscriptionType,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import SubscriptionAction from "./subscription-action";
import {
  updateCancelAtEndOfPeriod,
  updatePaymentMethod,
  updateTaxExemption,
} from "api/endpoints/subscription";
import { callOptionsHeaders } from "api/helpers";
import ConfirmDialog from "components/shared/confirm-dialog";
import { FEATURE_FLAG_CREDIT_CARD_LESS_EXPERIENCE } from "constants/billing-experiences";
import {
  DEFAULT_SNACKBAR_DURATION,
  EventTypeReason,
  NO_DISPLAY_DATA,
  subscriptionPaymentMethodDisplayMapping,
} from "constants/index";
import { UseGetFeatureFlagValue } from "context/feature-flags-context";
import { OrgDataContext } from "context/organisation-context";
import { SnackbarContext } from "context/snackbar-context";
import { toUUID } from "helpers/s12-helpers";
import { UrlParamTypes } from "types/org-display";

interface UpdatePaymentCollectionMethodProps {
  hideTaxExemptCheckbox?: boolean;
}

const UpdatePaymentCollectionMethod = ({
  hideTaxExemptCheckbox = false,
}: UpdatePaymentCollectionMethodProps) => {
  const theme = useTheme();
  const { regionCode, roleId } = useParams<UrlParamTypes>();
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);
  const [openSuccessAlert, setOpenSuccessAlert] = useState(false);

  const { orgData, setOrgData } = useContext(OrgDataContext);
  const { showError } = useContext(SnackbarContext);
  const hasCCLExp = UseGetFeatureFlagValue(
    FEATURE_FLAG_CREDIT_CARD_LESS_EXPERIENCE
  );

  const subscription = orgData.subscription[0];
  const currentPaymentMethod = subscription?.chargify?.paymentMethod;

  const [targetPaymentMethod, setTargetPaymentMethod] = useState(
    currentPaymentMethod === PaymentMethod.AUTOMATIC
      ? PaymentMethod.INVOICE
      : PaymentMethod.AUTOMATIC
  );
  const [taxExemptCheckboxStatus, setTaxExemptCheckboxStatus] = useState(true);

  const isFreeOrg =
    subscription &&
    (subscription.type === SubscriptionType.FREE ||
      subscription.type === SubscriptionType.FREE_ORG);

  const hasPaymentMethod = subscription?.chargify?.paymentMethod !== undefined;

  const isButtonDisabled = !hasPaymentMethod || isFreeOrg;

  const haveCreditCard =
    subscription?.chargify?.billingDetails?.maskedCardNumber !== "";

  const isTrialing = subscription?.chargify?.state === ChargifyState.TRIALING;

  const taxExemptionStatus = () => {
    if (subscription?.chargify?.paymentMethod === undefined) {
      return NO_DISPLAY_DATA;
    }
    return targetPaymentMethod === PaymentMethod.INVOICE;
  };

  const taxExemptionStatusMessage = () => {
    return (
      <>
        Set tax exemption to{" "}
        <b>{taxExemptionStatus().toString().toUpperCase()}</b>
      </>
    );
  };

  const handlePaymentMethodStateUpdate = () => {
    const newOrgData = { ...orgData } as Role;
    if (newOrgData.subscription[0].chargify) {
      newOrgData.subscription[0].chargify.paymentMethod = targetPaymentMethod;
      setOrgData(newOrgData);
    }
  };

  const handleUpdatePaymentMethod = () => {
    return updatePaymentMethod(
      {
        subscriptionId: subscription.id,
        paymentMethod: targetPaymentMethod,
        organizationId: orgData.id,
        chargifyState:
          subscription?.chargify?.state || ChargifyState.UNSPECIFIED,
      },
      callOptionsHeaders(regionCode, orgData.id)
    )
      .then(() => {
        handlePaymentMethodStateUpdate();
        const newTarget =
          targetPaymentMethod === PaymentMethod.AUTOMATIC
            ? PaymentMethod.INVOICE
            : PaymentMethod.AUTOMATIC;
        setTargetPaymentMethod(newTarget);
      })
      .catch(showError);
  };

  const handleTaxExemptStateUpdate = (newTaxExempt: boolean) => {
    const newOrgData = { ...orgData } as Role;
    if (newOrgData.subscription && newOrgData.subscription[0].chargify) {
      newOrgData.subscription[0].chargify.taxExempt = newTaxExempt;
      setOrgData(newOrgData);
    }
  };

  const handleUpdateTaxExemption = () => {
    const taxExemptStatus = targetPaymentMethod === PaymentMethod.INVOICE;
    return updateTaxExemption(
      {
        organizationId: toUUID(roleId),
        isTaxExempt: taxExemptStatus,
      },
      callOptionsHeaders(regionCode, orgData.id)
    )
      .then(() => {
        handleTaxExemptStateUpdate(taxExemptStatus);
      })
      .catch(showError);
  };

  const handleCancelAtEndOfPeriodStateUpdate = (
    cancelAtEndOfPeriod: boolean
  ) => {
    const newOrgData = { ...orgData } as Role;
    if (newOrgData.subscription && newOrgData.subscription[0].chargify) {
      newOrgData.subscription[0].chargify.cancelAtEndOfPeriod =
        cancelAtEndOfPeriod;
      setOrgData(newOrgData);
    }
  };

  const handleCancelAtEndOfPeriod = () => {
    const cancelAtEndOfPeriod = targetPaymentMethod === PaymentMethod.AUTOMATIC;
    const message = cancelAtEndOfPeriod ? "Credit card less trial" : "";

    return updateCancelAtEndOfPeriod(
      {
        organizationId: toUUID(roleId),
        subscriptionId: subscription.id,
        cancelAtEndOfPeriod,
        message,
      },
      callOptionsHeaders(regionCode, roleId)
    )
      .then(() => {
        handleCancelAtEndOfPeriodStateUpdate(cancelAtEndOfPeriod);
      })
      .catch(showError);
  };

  const resetState = () => {
    setTaxExemptCheckboxStatus(true);
  };

  const handleSubmit = async () => {
    setShowLoadingSpinner(true);
    await handleUpdatePaymentMethod();
    if (!hideTaxExemptCheckbox && taxExemptCheckboxStatus) {
      await handleUpdateTaxExemption();
    }
    if (hasCCLExp && isTrialing && !haveCreditCard) {
      await handleCancelAtEndOfPeriod();
    }
    setOpenSuccessAlert(true);
    setShowLoadingSpinner(false);
  };

  const handleClose = (
    _event?: Event | SyntheticEvent<unknown, Event>,
    reason?: string | undefined
  ) => {
    if (reason === EventTypeReason.Clickaway) {
      return;
    }
    setOpenSuccessAlert(false);
  };

  const warnContainerSx = {
    background: "#ffd4d4",
    border: "1px solid #941c1c",
    color: "#941c1c",
    borderRadius: 1,
    padding: theme.spacing(1.25),
    margin: "6px 4px 0px",
  };

  const displayCancelTrialEndOfPeriodMessage = () => {
    const cancelAtEndOfPeriodStatus =
      targetPaymentMethod === PaymentMethod.AUTOMATIC ? "set" : "remove";
    const paymentMethodMessage =
      subscriptionPaymentMethodDisplayMapping[
        targetPaymentMethod
      ].toUpperCase();

    return (
      <Grid item xs={12} data-testid={"cancel-at-end-of-period-status-message"}>
        <Box sx={warnContainerSx}>
          <ListItem sx={{ display: "list-item", padding: "0px 0px" }}>
            This organization is on the credit card less (CCL) trial.
          </ListItem>
          <ListItem sx={{ display: "list-item", padding: "0px 0px" }}>
            There is no payment method attached.
          </ListItem>
          <ListItem sx={{ display: "list-item", padding: "0px 0px" }}>
            Changing to <b>{paymentMethodMessage}</b> will{" "}
            <b>{cancelAtEndOfPeriodStatus} </b> the cancel at end of period
            status.
          </ListItem>
        </Box>
      </Grid>
    );
  };

  return (
    <React.Fragment>
      <SubscriptionAction
        title="Payment Collection Method:"
        displayInfo={
          !(subscription?.chargify?.paymentMethod === undefined)
            ? subscriptionPaymentMethodDisplayMapping[
                subscription?.chargify?.paymentMethod
              ]
            : NO_DISPLAY_DATA
        }
      >
        {showLoadingSpinner ? (
          <Grid container justifyContent="center" sx={{ mt: 1, mr: 2 }}>
            <CircularProgress
              color="primary"
              variant="indeterminate"
              thickness={4}
              size={30}
            />
          </Grid>
        ) : (
          <Button
            sx={{ mt: 1, mr: 2 }}
            variant="contained"
            color="primary"
            disabled={isButtonDisabled}
            fullWidth
            onClick={() => setOpenConfirmDialog(true)}
            data-testid="change-payment-method-btn"
          >
            CHANGE TO{" "}
            {subscriptionPaymentMethodDisplayMapping[targetPaymentMethod]}
          </Button>
        )}
      </SubscriptionAction>

      <ConfirmDialog
        title="Change Payment Collection Method"
        cancelBtnLabel="Cancel"
        confirmBtnLabel="Confirm"
        width="md"
        open={openConfirmDialog}
        setOpen={setOpenConfirmDialog}
        onConfirm={() => {
          handleSubmit();
        }}
        onCancel={resetState}
      >
        <Grid container spacing={1}>
          {hasCCLExp && isTrialing && !haveCreditCard
            ? displayCancelTrialEndOfPeriodMessage()
            : null}

          <Grid item xs={12}>
            <Typography>
              Are you sure you want to change the payment collection method to{" "}
              {""}
              <b>
                {subscriptionPaymentMethodDisplayMapping[
                  targetPaymentMethod
                ].toUpperCase()}
              </b>
              ?
            </Typography>
          </Grid>

          {!hideTaxExemptCheckbox && (
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={taxExemptCheckboxStatus}
                    disableRipple={true}
                    size={"small"}
                    onChange={() =>
                      setTaxExemptCheckboxStatus(!taxExemptCheckboxStatus)
                    }
                    color="primary"
                    inputProps={
                      {
                        "data-testid": "tax-exempt-checkbox",
                        // eslint-disable-next-line
                      } as any
                    }
                  />
                }
                label={taxExemptionStatusMessage()}
              />
            </Grid>
          )}
        </Grid>
      </ConfirmDialog>

      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        open={openSuccessAlert}
        autoHideDuration={DEFAULT_SNACKBAR_DURATION}
        onClose={handleClose}
      >
        <Alert severity="success" variant="filled">
          Payment collection method updated
        </Alert>
      </Snackbar>
    </React.Fragment>
  );
};

export default UpdatePaymentCollectionMethod;
